/** One payoff milestone on the debt journey, keyed by the share of the balance paid. */
export interface DebtMilestone {
  id: string;
  title: string;
  description: string;
  threshold: number; // percentage of debt paid
  achieved: boolean;
  achievedDate?: string;
  icon?: 'trophy' | 'star' | 'rocket' | 'target' | 'medal' | 'crown';
}

/**
 * Build the 5 standard debt-freedom milestones from balance data.
 * Shared across production pages to avoid divergence.
 */
export function buildDebtMilestones(totalOriginal: number, totalCurrent: number): DebtMilestone[] {
  if (totalOriginal <= 0) return [];

  const paidOff = totalOriginal - totalCurrent;
  const paidPercent = (paidOff / totalOriginal) * 100;
  const firstTarget = Math.min(5000, totalOriginal);

  return [
    {
      id: '1',
      title: 'First $5k',
      description: 'Paid first $5,000',
      threshold: (firstTarget / totalOriginal) * 100,
      achieved: paidOff >= firstTarget,
      achievedDate: paidOff >= firstTarget ? 'Achieved!' : undefined,
      icon: 'rocket',
    },
    {
      id: '2',
      title: '25% Done',
      description: 'Quarter paid off',
      threshold: 25,
      achieved: paidPercent >= 25,
      achievedDate: paidPercent >= 25 ? 'Achieved!' : undefined,
      icon: 'star',
    },
    {
      id: '3',
      title: 'Halfway',
      description: '50% to freedom',
      threshold: 50,
      achieved: paidPercent >= 50,
      icon: 'target',
    },
    {
      id: '4',
      title: '75% Done',
      description: 'Three quarters there',
      threshold: 75,
      achieved: paidPercent >= 75,
      icon: 'medal',
    },
    {
      id: '5',
      title: 'Debt Free!',
      description: '$0 balance',
      threshold: 100,
      achieved: totalCurrent <= 0,
      icon: 'crown',
    },
  ];
}

/**
 * Compute the percentage of debt paid off, clamped to [0, 100].
 */
export function computeProgressPercent(totalOriginal: number, totalCurrent: number): number {
  if (totalOriginal <= 0) return 0;
  const paid = totalOriginal - totalCurrent;
  return Math.min(100, Math.max(0, Math.round((paid / totalOriginal) * 100)));
}
